<template>
  <div class="Realisation Record">
    <div
      v-if="isForbidden"
      class="d-flex w-100 align-items-center justify-content-center"
      style="height: 50vh"
    >
      <h2 class="mr-3 mb-0">
        401
      </h2>
      <div>{{ $t('Process.401-cpo') }}</div>
    </div>
    <div
      v-else-if="notFound"
      class="d-flex w-100 align-items-center justify-content-center"
      style="height: 50vh"
    >
      <h2 class="mr-3 mb-0">
        404
      </h2>
      <div>{{ $t('Process.404-notFound') }}</div>
    </div>
    <div
      v-else-if="isNewProcess"
      class="d-flex justify-content-center flex-gap-2"
    >
      <StartNewCustomProcess />
    </div>
    <div
      v-else-if="getSelectedProcess"
      class="d-flex justify-content-center flex-gap-2"
    >
      <CustomProcessMap
        v-if="!isTakeoverProcess"
        :key="componentKey"
        :connection-enabled="isLocationConnected"
        :class="['process-map', { 'hidden': mapHidden }, { 'active': getShowMap }, { isSticky }]"
        :asset-process-type="assetProcessType"
        :map-hidden="mapHidden"
        @isLocationConnected="event => isLocationConnected = event"
        @currentRecord="record => (currentRecord = record, correction = false)"
        @feedback="event => (feedback = event.feedback, feedbackMessage = event.message)"
        @handleInput="handleInput"
        @handleCancelChange="event => locked = event"
      />
      <div
        class="process-main"
      >
        <RealisationHeader
          :record="record"
          :feedback="feedback"
          :feedback-message="feedbackMessage"
          :step="step"
          :record-step="currentStep"
          :may-edit="mayEdit && isCompletedStep"
          @dismissed="(feedback = null, feedbackMessage = '')"
        >
          <FlowInteraction
            :key="flowInteractionKey"
            :first="currentStep === 1"
            :last="isFinalStep"
            :step="step"
            :current-step="currentStep"
            :locked="locked"
            :disabled="disabled"
            :completed="completed"
            :is-completed-step="isCompletedStep"
            :disable-next-btn="disableNextBtn"
            :may-edit="mayEdit"
            :open-for-change="openForChange"
            :record="record"
            :location-connected="isLocationConnected"
            @handleNext="handleNext"
            @handlePrevious="handlePrevious"
            @handleSubmitAndNext="handleSubmitAndNext"
            @handleCancelChange="handleCancelChange"
            @handleStartCorrection="handleStartCorrection"
            @handleStartChange="handleStartChange"
            @handleComplete="handleComplete"
            @handleSubmit="saveRecord"
          />
        </RealisationHeader>
        <router-view
          :key="`${routerViewKey}-${$route.path}`"
          class="process-form"
          @currentRecord="record => (currentRecord = record, correction = false)"
          @feedback="event => (feedback = event.feedback, feedbackMessage = event.message, locked = false)"
          @handleInput="handleInput"
          @handleCancelChange="event => locked = event"
        />
      </div>
      <ProcessSidebar
        :uuid="uuid || ''"
        :disabled="disabled || locked"
        :style="getShowMap && mapHidden ? 'display:none' : ''"
      />
    </div>
    <div
      v-else
      class="spinner-full-window d-flex"
    >
      <b-spinner
        variant="secondary"
        class="spinner"
      />
    </div>
    <div
      v-if="getIsSavingProcess"
      class="spinner-full-window d-flex"
    >
      <b-spinner
        variant="primary"
        class="spinner"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getProcessByUuid, fetchBaseAssetProcess, fetchAssetByLocationId } from '@/services/processes'
import ProcessSidebar from '@/components/realisation/ProcessSidebar';
import RealisationHeader from '@/components/realisation/RealisationHeader.vue';
import FlowInteraction from '@/components/flow/FlowInteraction.vue';
import CustomBaseStep from '@/views/processes/CustomBaseStep.vue';
import CustomProcessMap from '@/views/CustomProcessMap';
import StartNewCustomProcess from '@/views/StartNewCustomProcess.vue';

export default {
  name: 'Record',
  components: {
    ProcessSidebar,
    RealisationHeader,
    FlowInteraction,
    CustomProcessMap,
    StartNewCustomProcess
  },
  mixins: [CustomBaseStep],
  data() {
    return {
      isLocationConnected: true,
      filteringStorePoints: [
        'chargingpoints/filterChargingPoints',
        'realisations/filterRealisations',
        'requests/filterRequests'
      ],
      processType: '',
      //record: null,~
      isForbidden: false,
      notFound: false,
      componentKey: 0,
      isSaving: false,
      mapHidden: false,
      isSticky: false,
      token: '',
      hasValidRoute: false,
      routerViewKey: 0,
      flowInteractionKey: 0
    };
  },
  computed: {
    ...mapGetters('user', ['canAccessRealisationProcesses', 'hasRole']),
    ...mapGetters('tenant', ['getActiveProcessTypes', 'getSteps']),
    ...mapGetters('processes', ['getSelectedProcess', 'getSelectedAsset', 'getCurrentProcessType', 'getShowMap', 'getRecordLocked', 'getIsSavingProcess']),


    isNewProcess() {
      return this.$route.name?.startsWith('new-') || this.$route.params?.uuid?.startsWith('new-');
    },
    isSelectedProcess() {
      return this.getSelectedProcess?.locationId?.full === this.$route.params.locationId
        || !this.isNewProcess && (this.getSelectedProcess?.uuid === this.$route.params.uuid)
    },
    assetProcessType() {
      return this.$route.params.type &&
        this.getActiveProcessTypes.find(process => process.label.toLowerCase() === this.$route.params.type)
    },
    isTakeoverProcess() {
      return this.getSelectedProcess?.processType?.value === 'takeover'
    },
    isActiveAssetProcessCancelled () {
      if (!this.getSelectedAsset) return;

      return this.getSelectedAsset.processes.at(0)?.processStatus?.status === 'cancelled'
    },
    userIdentity () {
      const identity = this.$auth.user['https://evtools.nl/identity']
      return identity?.uuid ? identity : {}
    },
    currentProcessRouteType () {
      return this.getActiveProcessTypes?.find(type => type.routeType === this.$route.params?.type)?.value
    },
    isFinalStep () {
      return this.currentStepData?.final === true
    }
  },
  watch: {
    $route: {
      async handler () {
        this.feedback = null;
        this.feedbackMessage = ''
        this.$store.dispatch('processes/isSavingProcess', false)
        //this.locked = false

        if (!this.isNewProcess && (!this.getSelectedProcess || (this.getSelectedProcess?.uuid !== this.$route.params?.uuid))) { // Push to process from map //
          await this.getProcess()
        }
        const processType = this.getSelectedProcess?.processType?.value
        if (processType) {
          this.$store.dispatch('processes/setCurrentProcessType', {
            processType: this.getSelectedProcess?.processType?.value
          })
        }
      }
    },
    getShowMap: {
      handler (value) {
        if (!value) return this.isSticky = false

        setTimeout(() => {
          this.isSticky = value
        }, 300)
      }
    },
    getRecordLocked: {
      immediate: true,
      handler (locked) {
        if (locked) {
          // TEMP: After cancelling correction, reset "locked" of current step component //
          this.routerViewKey++
        }
        this.locked = locked
      }
    },
    getIsSavingProcess: {
      handler () {
        this.flowInteractionKey++
      }
    },
  },

  async created() {
    // If new process route, redirect if process type doesn't match //
    if (this.isNewProcess && !this.$route.name.startsWith('new')) {
      const processType = this.getActiveProcessTypes.find(type => type.value === this.getCurrentProcessType);
      if (processType) {
        await this.$router.replace({
          name: `new-${processType?.value}`
        });
      }
    }

    this.mapHidden = window.innerWidth <= 2100
    this.token = await this.$auth.getTokenSilently()
    // Remove Realisation process from store and reset possible selectedProcess //
    this.$store.dispatch('realisations/setSelectedRecord', { record: null })
    this.$store.dispatch('processes/setSelectedProcess', { process: null })
    this.$store.dispatch('chargingpoints/setActiveMunicipality', { code: '' })
    this.$store.dispatch('chargingpoints/resetRecords')

    if (this.isNewProcess) {
      const locationId = this.$route.params.locationId

      if (this.assetProcessType) {
        await fetchBaseAssetProcess({ token: this.token, locationId, processType: this.assetProcessType })
          .catch(err => console.log(err))
      }
    } else {
      await this.getProcess()
      this.$store.dispatch('processes/isSavingProcess', false)
    }

    const selectedAsset = this.getSelectedAsset?.locationId === this.getSelectedProcess?.locationId?.full
    // console.log('Process', selectedAsset, this.getSelectedProcess, this.getSelectedAsset)
    if (!selectedAsset && this.getSelectedProcess && this.getSelectedProcess?.locationId) {
      await fetchAssetByLocationId({ token: this.token, locationId: this.getSelectedProcess?.locationId?.full })
        .catch(err => console.log(err))
    }

    const processType = this.getSelectedProcess?.processType?.value || this.$route.params.uuid?.split('-')?.pop()
    if (processType) {
      this.$store.dispatch('processes/setCurrentProcessType', { processType })
    }

    //this.record = this.getSelectedProcess
    this.componentKey++

    const code = this.record.MunicipalityCode
    this.$store.dispatch('chargingpoints/setActiveMunicipality', { code })



    if (this.isNewProcess && this.isActiveAssetProcessCancelled) {
      this.$store.dispatch('tenant/setToast', {
        message: this.$i18n.t('LocationList.cancelledProcessMessage'),
        error: false,
        variant: 'warning',
        delay: 1000
      });
    }

    if (this.isNewProcess || this.$route.params.step === this.currentStep) return;

    if (this.$route.params?.uuid && this.getSelectedProcess && !(this.$route.params.step || this.$route.query.step)) {
      const processType = this.getSelectedProcess.processType
      const currentStep = this.currentStep === 100 ? this.lastStep : this.currentStep

      if (currentStep === +this.$route.query.step) return;

      this.$router.replace({
        name: `${processType.value}Steps`,
        params: { type: processType.routeType, uuid: this.$route.params.uuid },
        query: { step: currentStep }
      });
    }
  },
  mounted() {
    window.addEventListener('resize', this.setMapState)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setMapState)
  },
  methods: {
    setMapState (e) {
      this.mapHidden = e.target.innerWidth < 2100
    },

    async getProcess () {
      const uuid = this.$route.params.uuid
      if (!uuid) return

      this.$store.dispatch('processes/isSavingProcess', true)
      const process = await getProcessByUuid({ token: this.token, uuid, useClass: true })
        .catch(err => console.log(err))

      if (!process || process.errorMessage) {
        this.notFound = true
        return
      }

      this.$store.dispatch('chargingpoints/setActiveMunicipality', { code: process.MunicipalityCode })

      if (process && this.userIdentity?.uuid) {
        const category = this.userIdentity.category
        const isLinkedCpo = process.stakeholders.some(s => s.category === 'cpo' && s.uuid === this.userIdentity?.uuid)
        const processMunicipality = process.MunicipalityCode

        if (
          (category === 'cpo' && !isLinkedCpo) ||
          (category === 'municipality' && processMunicipality !== this.userIdentity.code)
        ) {
          return this.isForbidden = true
        }
      }

      this.$store.dispatch('processes/isSavingProcess', false)

      return process
    }
  }
};
</script>
